import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { companyAPI, settingsAPI, institutionAPI, BACKEND_URL } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { useSettings, useCompanyUsers, queryKeys } from '../lib/queries';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Calculator, FileText, Shield, Save, Loader2, UserPlus, Users, Trash2, Eye, EyeOff, Lock, LineChart, TrendingUp, HardHat, CheckCircle2, Sparkles, Zap } from 'lucide-react';
import { ROLE_MAP } from '../lib/utils';
import { useConfirm } from '../components/ui/confirm-dialog';
import { useModules } from '../lib/modules-context';
import { usePreferences } from '../lib/preferences-context';
import { toast } from 'sonner';

const PERMISSIONS = {
  owner: ['Acceso total a la plataforma', 'Gestionar empresa y usuarios', 'Crear y eliminar proyectos', 'Ejecutar análisis y reportes', 'Configurar parámetros globales'],
  analyst: ['Crear y editar proyectos y escenarios', 'Ejecutar análisis y sensibilidad', 'Generar reportes', 'Usar Copilot'],
  viewer: ['Ver proyectos y escenarios en modo lectura', 'Acceder a reportes generados'],
};

const PERM_MATRIX = [
  { feature: 'Crear proyectos', owner: 'full', analyst: 'full', viewer: 'none' },
  { feature: 'Editar escenarios', owner: 'full', analyst: 'full', viewer: 'none' },
  { feature: 'Ejecutar análisis', owner: 'full', analyst: 'full', viewer: 'none' },
  { feature: 'Generar reportes', owner: 'full', analyst: 'full', viewer: 'read' },
  { feature: 'Usar Copilot', owner: 'full', analyst: 'full', viewer: 'none' },
  { feature: 'Configuración', owner: 'full', analyst: 'none', viewer: 'none' },
  { feature: 'Gestionar usuarios', owner: 'full', analyst: 'none', viewer: 'none' },
];

function SectionTitle({ children }) {
  return <h3 className="text-xs font-display font-semibold uppercase tracking-wide text-muted-foreground mb-4" style={{ letterSpacing: '0.8px' }}>{children}</h3>;
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function PctInput({ value, onChange, ...props }) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');
  const display = value != null ? `${Math.round(value * 10000) / 100}%` : '';
  return (
    <Input className="h-9 text-right tabular-nums" {...props}
      value={editing ? raw : display}
      onFocus={() => { setEditing(true); setRaw(value != null ? String(Math.round(value * 10000) / 100) : ''); }}
      onBlur={() => { setEditing(false); const v = parseFloat(raw); if (!isNaN(v)) onChange(v / 100); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      onChange={(e) => setRaw(e.target.value)} />
  );
}

function InstitutionJoinSection() {
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const { refreshUser } = useAuth();

  const handleJoin = async () => {
    if (!code.trim()) { toast.error('Ingresa un código de invitación'); return; }
    setJoining(true);
    try {
      const res = await institutionAPI.join(code.trim());
      toast.success(res.data.message || 'Empresa vinculada exitosamente');
      setCode('');
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código inválido o expirado');
    }
    setJoining(false);
  };

  return (
    <div data-testid="institution-join-section">
      <SectionTitle>Institución</SectionTitle>
      <div className="p-4 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)' }}>
        <p className="text-sm font-medium mb-1">Unirse a una institución</p>
        <p className="text-[11px] text-muted-foreground mb-3">Si recibiste un código de invitación de un fondo o holding, ingrésalo aquí para vincular tu empresa a su portfolio institucional.</p>
        <div className="flex gap-2">
          <Input
            placeholder="Código de invitación"
            value={code}
            onChange={e => setCode(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleJoin(); }}
            className="h-9 font-mono"
            data-testid="join-code-input"
          />
          <Button size="sm" className="shrink-0 h-9 text-white" style={{ background: 'var(--accent-primary)' }} onClick={handleJoin} disabled={joining} data-testid="join-institution-btn">
            {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Vincular'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CompanyLogoUpload() {
  const [logo, setLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { refreshUser } = useAuth();

  // Load current logo
  useState(() => {
    companyAPI.getInfo().then(r => setLogo(r.data?.logo_url || null)).catch(() => {});
  });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo imágenes'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Máximo 2MB'); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await companyAPI.uploadLogo(formData);
      setLogo(res.data.logo_url);
      toast.success('Logo actualizado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setUploading(false);
  };

  const handleRemove = async () => {
    try {
      await companyAPI.deleteLogo();
      setLogo(null);
      toast.success('Logo eliminado');
    } catch { toast.error('Error'); }
  };

  const API_URL = BACKEND_URL;
  // SWA-112 · logo_url que apunta a un archivo inexistente → placeholder, no imagen rota
  const [logoBroken, setLogoBroken] = useState(false);
  const [brokenFor, setBrokenFor] = useState(null);
  const isBroken = logoBroken && brokenFor === logo;

  return (
    <div data-testid="company-logo-section">
      <SectionTitle>Logo de la Empresa</SectionTitle>
      <div className="flex items-center gap-5">
        <div className="w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden shrink-0" style={{ background: 'var(--bg-elevated)', border: '2px dashed var(--border-default)' }}>
          {logo && !isBroken ? (
            <img src={`${API_URL}${logo}`} alt="Logo" className="w-full h-full object-contain p-1.5"
              onError={() => { setLogoBroken(true); setBrokenFor(logo); }} data-testid="company-logo-img" />
          ) : (
            <Building2 className="w-8 h-8" style={{ color: 'var(--text-muted)', opacity: 0.3 }} data-testid="company-logo-fallback" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium mb-1">Logo de la empresa</p>
          <p className="text-[11px] text-muted-foreground mb-3">Se muestra en reportes y documentos. Formatos: JPG, PNG, WebP. Máximo 2MB.</p>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="logo-file-input" />
              <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium text-white cursor-pointer" style={{ background: 'var(--accent-primary)' }}>
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Building2 className="w-3 h-3" />}
                {logo ? 'Cambiar logo' : 'Subir logo'}
              </span>
            </label>
            {logo && (
              <Button variant="outline" size="sm" className="h-8 text-xs text-destructive" onClick={handleRemove} data-testid="remove-logo-btn">
                Eliminar
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function UserAuditLog({ users }) {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['auditLogs', 'user'],
    queryFn: () => companyAPI.getAuditLogs({ entity_type: 'user', limit: 50 }).then(r => r.data),
    staleTime: 30 * 1000,
  });
  const userById = users.reduce((acc, u) => { acc[u.id] = u; return acc; }, {});
  const ACTION_MAP = {
    create: { label: 'Creado', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    update: { label: 'Actualizado', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    delete: { label: 'Eliminado', color: 'bg-red-50 text-red-700 border-red-200' },
    lock: { label: 'Bloqueado', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    unlock: { label: 'Desbloqueado', color: 'bg-slate-50 text-slate-700 border-slate-200' },
  };
  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Cargando historial…</p>;
  }
  if (!logs.length) {
    return <p className="text-xs text-muted-foreground">Sin actividad registrada aún.</p>;
  }
  return (
    <div className="rounded-xl border border-border overflow-hidden" data-testid="user-audit-log">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30 text-[11px]">
            <TableHead>Fecha</TableHead>
            <TableHead>Ejecutado por</TableHead>
            <TableHead>Acción</TableHead>
            <TableHead>Usuario afectado</TableHead>
            <TableHead>Detalles</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log, i) => {
            const target = userById[log.entity_id];
            const meta = ACTION_MAP[log.action] || { label: log.action, color: 'bg-slate-50 text-slate-700 border-slate-200' };
            return (
              <TableRow key={log.id || i} className="text-xs">
                <TableCell className="tabular-nums text-muted-foreground">
                  {log.created_at ? new Date(log.created_at).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                </TableCell>
                <TableCell>{log.user_name || 'Sistema'}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {target ? `${target.full_name} (${target.email})` : `#${(log.entity_id || '').slice(0, 8)}…`}
                </TableCell>
                <TableCell className="text-muted-foreground max-w-[280px] truncate" title={log.changes ? JSON.stringify(log.changes) : ''}>
                  {log.changes ? Object.keys(log.changes).map(k => k).join(', ') : '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { setPref: setGlobalPref } = usePreferences();
  const { data: settingsData, isLoading: settingsLoading } = useSettings();
  const { data: users = [], isLoading: usersLoading } = useCompanyUsers();
  const [settings, setSettings] = useState(null);
  const loading = (settingsLoading || usersLoading) && !settings;
  const [saving, setSaving] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ email: '', full_name: '', role: 'analyst' });
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferPassword, setTransferPassword] = useState('');
  const [transferring, setTransferring] = useState(false);
  const confirm = useConfirm();
  const qc = useQueryClient();

  // Sync settings from React Query into local state for form editing
  if (settingsData && settings !== settingsData && !saving) setSettings(settingsData);

  const handleSave = async (section, data) => {
    setSaving(section);
    try {
      let res;
      if (section === 'company') res = await settingsAPI.updateCompany(data);
      else if (section === 'financial') res = await settingsAPI.updateFinancial(data);
      else if (section === 'reports') res = await settingsAPI.updateReports(data);
      if (res) setSettings(res.data);
      toast.success('Configuración guardada');
      // Refresh user profile so company name updates everywhere
      if (section === 'company' && data.business_name) {
        await refreshUser();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
    setSaving('');
  };

  const handleInvite = async () => {
    if (!invite.password || invite.password.length < 6) { toast.error('Contraseña mínima de 6 caracteres'); return; }
    try {
      await companyAPI.inviteUser(invite);
      toast.success('Usuario creado');
      setShowInvite(false);
      setInvite({ email: '', full_name: '', role: 'analyst', password: '' });
      const r = await companyAPI.listUsers();
      qc.setQueryData(queryKeys.companyUsers, r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDeleteUser = async (userId) => {
    const ok = await confirm({
      title: '¿Eliminar usuario?',
      description: 'Esta acción no se puede deshacer. El usuario perderá acceso inmediatamente.',
      confirmLabel: 'Eliminar',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await companyAPI.deleteUser(userId);
      qc.setQueryData(queryKeys.companyUsers, (prev = []) => prev.filter(u => u.id !== userId));
      toast.success('Usuario eliminado');
    } catch (err) {
      console.error('[Settings] deleteUser failed', err);
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleToggleUser = async (userId, isActive) => {
    try {
      await companyAPI.updateUser(userId, { is_active: !isActive });
      qc.setQueryData(queryKeys.companyUsers, (prev = []) => prev.map(u => u.id === userId ? { ...u, is_active: !isActive } : u));
      toast.success(isActive ? 'Usuario desactivado' : 'Usuario activado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleSaveUser = async (userId) => {
    try {
      await companyAPI.updateUser(userId, { role: editRole });
      qc.setQueryData(queryKeys.companyUsers, (prev = []) => prev.map(u => u.id === userId ? { ...u, role: editRole } : u));
      setEditingUser(null);
      toast.success('Rol actualizado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleTransferOwnership = async () => {
    if (!transferTarget || !transferPassword) return;
    setTransferring(true);
    try {
      await companyAPI.transferOwnership({ new_owner_id: transferTarget.id, password: transferPassword });
      toast.success(`Propiedad transferida a ${transferTarget.full_name}`);
      setTransferTarget(null);
      setTransferPassword('');
      // Refresh data
      const res = await companyAPI.listUsers();
      qc.setQueryData(queryKeys.companyUsers, res.data);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al transferir');
    }
    setTransferring(false);
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = !userSearch || u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase());
    const matchRole = userFilter === 'all' || u.role === userFilter;
    return matchSearch && matchRole;
  });

  const handleChangePassword = async () => {
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    if (pwForm.newPw.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setSaving('password');
    try {
      await settingsAPI.changePassword({ current_password: pwForm.current, new_password: pwForm.newPw });
      toast.success('Contraseña actualizada');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving('');
  };

  const s = settings || {};
  const updateField = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (loading) return <div className="px-6 py-6"><div className="animate-pulse space-y-4"><div className="h-8 w-48 bg-muted rounded" /><div className="h-64 bg-muted rounded-xl" /></div></div>;

  return (
    <div className="h-full overflow-y-auto" data-testid="settings-page">
      <div className="px-6 lg:px-8 py-6 max-w-[960px]">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <div className="mb-6">
            <h1 className="font-display text-2xl font-semibold tracking-tight">Configuración</h1>
            <p className="text-sm text-muted-foreground mt-1">Administra tu empresa, parámetros financieros y seguridad</p>
          </div>

          <Tabs defaultValue="company">
            <TabsList className="mb-6">
              <TabsTrigger value="company" className="gap-1.5"><Building2 className="h-3.5 w-3.5" /> Empresa</TabsTrigger>
              <TabsTrigger value="financial" className="gap-1.5"><Calculator className="h-3.5 w-3.5" /> Financiero</TabsTrigger>
              <TabsTrigger value="reports" className="gap-1.5"><FileText className="h-3.5 w-3.5" /> Reportes</TabsTrigger>
              <TabsTrigger value="users" className="gap-1.5"><Users className="h-3.5 w-3.5" /> Usuarios</TabsTrigger>
              <TabsTrigger value="security" className="gap-1.5"><Shield className="h-3.5 w-3.5" /> Seguridad</TabsTrigger>
            </TabsList>

            {/* ═══ COMPANY ═══ */}
            <TabsContent value="company">
              <Card className="rounded-xl border-border/50 shadow-sm">
                <CardContent className="p-6 space-y-6">
                  {/* Company Logo */}
                  <CompanyLogoUpload />
                  <Separator />
                  <SectionTitle>Datos de la Empresa</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    <Field label="Nombre comercial" hint="Se usa en la navegación y reportes">
                      <Input value={s.business_name || ''} onChange={(e) => updateField('business_name', e.target.value)} />
                    </Field>
                    <Field label="Razón social">
                      <Input value={s.legal_name || ''} onChange={(e) => updateField('legal_name', e.target.value)} />
                    </Field>
                    <Field label="País">
                      <Select value={s.country || 'México'} onValueChange={(v) => updateField('country', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="México">México</SelectItem><SelectItem value="Colombia">Colombia</SelectItem><SelectItem value="Chile">Chile</SelectItem><SelectItem value="Perú">Perú</SelectItem><SelectItem value="Argentina">Argentina</SelectItem></SelectContent>
                      </Select>
                    </Field>
                    <Field label="Zona horaria">
                      <Select value={s.timezone || 'America/Mexico_City'} onValueChange={(v) => updateField('timezone', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="America/Mexico_City">Ciudad de México</SelectItem><SelectItem value="America/Monterrey">Monterrey</SelectItem><SelectItem value="America/Tijuana">Tijuana</SelectItem><SelectItem value="America/Bogota">Bogotá</SelectItem></SelectContent>
                      </Select>
                    </Field>
                    <Field label="Moneda por defecto">
                      <Select value={s.default_currency || 'MXN'} onValueChange={(v) => updateField('default_currency', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="MXN">MXN — Peso Mexicano</SelectItem><SelectItem value="USD">USD — Dólar Americano</SelectItem><SelectItem value="UDI">UDI</SelectItem></SelectContent>
                      </Select>
                    </Field>
                    <Field label="Idioma">
                      <Select value={s.language || 'es'} onValueChange={(v) => updateField('language', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="es">Español</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button onClick={() => handleSave('company', { business_name: s.business_name, legal_name: s.legal_name, country: s.country, timezone: s.timezone, default_currency: s.default_currency, language: s.language })} disabled={saving === 'company'}
                      style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90">
                      {saving === 'company' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Guardar
                    </Button>
                  </div>
                  <Separator className="my-4" />

                  {/* Institution section */}
                  {user?.institution_id ? (
                    <div>
                      <SectionTitle>Institución</SectionTitle>
                      <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--accent-subtle)', border: '1px solid var(--border-default)' }}>
                        <Building2 className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                        <div>
                          <p className="text-sm font-medium">Vinculada a una institución</p>
                          <p className="text-[11px] text-muted-foreground">Tu empresa forma parte de un portfolio institucional. El administrador de la institución puede ver métricas consolidadas.</p>
                        </div>
                        <Badge className="ml-auto text-[10px]" style={{ background: 'var(--accent-subtle)', color: 'var(--accent-primary)', border: '1px solid var(--accent-primary)30' }}>Enterprise</Badge>
                      </div>
                    </div>
                  ) : (
                    <InstitutionJoinSection />
                  )}

                  <Separator className="my-4" />
                  <SectionTitle>Preferencias de Interfaz</SectionTitle>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Tooltips educativos</Label>
                      <p className="text-[11px] text-muted-foreground">Muestra explicaciones al pasar el cursor sobre indicadores financieros (TIR, VAN, ROI, etc.)</p>
                    </div>
                    <Switch
                      checked={settings?.show_tooltips !== false}
                      onCheckedChange={async (checked) => {
                        setSettings(prev => ({ ...prev, show_tooltips: checked }));
                        setGlobalPref('show_tooltips', checked);
                        try { await settingsAPI.updatePreferences({ show_tooltips: checked }); toast.success(checked ? 'Tooltips activados' : 'Tooltips desactivados'); } catch (err) { console.error('[Settings] updatePreferences failed', err); }
                      }}
                      data-testid="toggle-tooltips"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ═══ SUBSCRIPTION / LICENCIAS ═══ */}
            {/* ═══ FINANCIAL ═══ */}
            <TabsContent value="financial">
              <Card className="rounded-xl border-border/50 shadow-sm">
                <CardContent className="p-6 space-y-6">
                  <SectionTitle>Parámetros Financieros por Defecto</SectionTitle>
                  <p className="text-xs text-muted-foreground -mt-2">Estos valores se usan como defaults al crear nuevos escenarios.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                    <Field label="Tasa de descuento" hint="WACC para cálculo de VAN">
                      <PctInput value={s.default_discount_rate} onChange={(v) => updateField('default_discount_rate', v)} />
                    </Field>
                    <Field label="TIR objetivo mínima" hint="Referencia para alertas y diagnóstico">
                      <PctInput value={s.target_irr_min} onChange={(v) => updateField('target_irr_min', v)} />
                    </Field>
                    <Field label="Inflación general" hint="INPC anual esperado">
                      <PctInput value={s.default_inflation} onChange={(v) => updateField('default_inflation', v)} />
                    </Field>
                    <Field label="Inflación de construcción" hint="Índice de costos de construcción">
                      <PctInput value={s.default_construction_inflation} onChange={(v) => updateField('default_construction_inflation', v)} />
                    </Field>
                    <Field label="Contingencia por defecto" hint="Reserva sobre costos directos">
                      <PctInput value={s.default_contingency} onChange={(v) => updateField('default_contingency', v)} />
                    </Field>
                  </div>
                  <Separator />
                  <SectionTitle>Rangos de Sensibilidad</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-5">
                    <Field label="Precio (±)"><PctInput value={s.sensitivity_price_range} onChange={(v) => updateField('sensitivity_price_range', v)} /></Field>
                    <Field label="Costo (±)"><PctInput value={s.sensitivity_cost_range} onChange={(v) => updateField('sensitivity_cost_range', v)} /></Field>
                    <Field label="Tasa (±)"><PctInput value={s.sensitivity_rate_range} onChange={(v) => updateField('sensitivity_rate_range', v)} /></Field>
                    <Field label="Absorción (±)"><PctInput value={s.sensitivity_absorption_range} onChange={(v) => updateField('sensitivity_absorption_range', v)} /></Field>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button onClick={() => handleSave('financial', { default_discount_rate: s.default_discount_rate, target_irr_min: s.target_irr_min, default_inflation: s.default_inflation, default_construction_inflation: s.default_construction_inflation, default_contingency: s.default_contingency, sensitivity_price_range: s.sensitivity_price_range, sensitivity_cost_range: s.sensitivity_cost_range, sensitivity_rate_range: s.sensitivity_rate_range, sensitivity_absorption_range: s.sensitivity_absorption_range })} disabled={saving === 'financial'}
                      style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90">
                      {saving === 'financial' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Guardar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ═══ REPORTS ═══ */}
            <TabsContent value="reports">
              <Card className="rounded-xl border-border/50 shadow-sm">
                <CardContent className="p-6 space-y-6">
                  <SectionTitle>Configuración de Reportes</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    <Field label="Nombre de empresa en portada" hint="Se muestra en el header de todos los reportes">
                      <Input value={s.report_company_name || ''} onChange={(e) => updateField('report_company_name', e.target.value)} />
                    </Field>
                    <Field label="Pie de página" hint="Texto al final de cada reporte">
                      <Input value={s.report_footer_text || ''} onChange={(e) => updateField('report_footer_text', e.target.value)} />
                    </Field>
                    <Field label="Email de contacto">
                      <Input type="email" value={s.report_contact_email || ''} onChange={(e) => updateField('report_contact_email', e.target.value)} />
                    </Field>
                    <Field label="Teléfono de contacto">
                      <Input value={s.report_contact_phone || ''} onChange={(e) => updateField('report_contact_phone', e.target.value)} />
                    </Field>
                    <Field label="Formato de exportación por defecto">
                      <Select value={s.report_default_format || 'pdf'} onValueChange={(v) => updateField('report_default_format', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="pdf">PDF</SelectItem><SelectItem value="excel">Excel</SelectItem></SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button onClick={() => handleSave('reports', { report_company_name: s.report_company_name, report_footer_text: s.report_footer_text, report_contact_email: s.report_contact_email, report_contact_phone: s.report_contact_phone, report_default_format: s.report_default_format })} disabled={saving === 'reports'}
                      style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90">
                      {saving === 'reports' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Guardar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ═══ USERS ═══ */}
            <TabsContent value="users">
              <Card className="rounded-xl border-border/50 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <SectionTitle>Equipo</SectionTitle>
                      <p className="text-xs text-muted-foreground -mt-2">{users.length} usuarios registrados</p>
                    </div>
                    <Dialog open={showInvite} onOpenChange={setShowInvite}>
                      <DialogTrigger asChild><Button size="sm" style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90"><UserPlus className="h-4 w-4 mr-1.5" /> Invitar usuario</Button></DialogTrigger>
                      <DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle className="font-display">Invitar usuario</DialogTitle></DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="grid grid-cols-2 gap-4">
                            <Field label="Nombre completo"><Input value={invite.full_name} onChange={(e) => setInvite({...invite, full_name: e.target.value})} placeholder="Juan Pérez" /></Field>
                            <Field label="Correo electrónico"><Input type="email" value={invite.email} onChange={(e) => setInvite({...invite, email: e.target.value})} placeholder="juan@empresa.com" /></Field>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <Field label="Rol">
                              <Select value={invite.role} onValueChange={(v) => setInvite({...invite, role: v})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{Object.entries(ROLE_MAP).filter(([k]) => k !== 'owner' && k !== 'super_admin').map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                              </Select>
                            </Field>
                            <Field label="Contraseña temporal" hint="Mínimo 6 caracteres"><Input type="password" value={invite.password || ''} onChange={(e) => setInvite({...invite, password: e.target.value})} placeholder="••••••" /></Field>
                          </div>
                          {invite.role && (
                            <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                              <p className="text-[10px] font-semibold uppercase text-muted-foreground mb-1.5">Permisos del rol: {ROLE_MAP[invite.role]}</p>
                              <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-muted-foreground">
                                {PERMISSIONS[invite.role]?.map(p => <span key={p}>• {p}</span>)}
                              </div>
                            </div>
                          )}
                        </div>
                        <DialogFooter><Button variant="outline" onClick={() => setShowInvite(false)}>Cancelar</Button><Button onClick={handleInvite} disabled={!invite.email || !invite.full_name || !invite.password}>Crear usuario</Button></DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {/* Search + filter */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="relative flex-1 max-w-xs">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input placeholder="Buscar por nombre o email..." className="pl-9 h-9" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
                    </div>
                    <Select value={userFilter} onValueChange={setUserFilter}>
                      <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="Todos los roles" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los roles</SelectItem>
                        {Object.entries(ROLE_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Table */}
                  <div className="rounded-xl border border-border overflow-hidden">
                    <Table>
                      <TableHeader><TableRow className="bg-muted/30 text-[11px]">
                        <TableHead>Usuario</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead className="text-center">Estado</TableHead>
                        <TableHead>Creado</TableHead>
                        <TableHead>Último acceso</TableHead>
                        <TableHead className="text-right w-[120px]">Acciones</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {filteredUsers.map(u => {
                          const isSelf = u.id === user?.id;
                          const isOwner = u.role === 'owner';
                          const isSuperAdmin = u.role === 'super_admin';
                          const isProtected = isOwner || isSuperAdmin;
                          return (
                            <TableRow key={u.id} className="text-sm">
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: isSelf ? 'var(--accent-primary)' : '#9CA3AF' }}>
                                    {(u.full_name || u.email || '?').charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{u.full_name}{isSelf && <span className="text-xs text-muted-foreground ml-1">(tú)</span>}</p>
                                    <p className="text-xs text-muted-foreground">{u.email}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                {editingUser === u.id ? (
                                  <Select value={editRole} onValueChange={setEditRole}>
                                    <SelectTrigger className="h-7 w-[140px]"><SelectValue /></SelectTrigger>
                                    <SelectContent>{Object.entries(ROLE_MAP).filter(([k]) => k !== 'owner' && k !== 'super_admin').map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                                  </Select>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px]"
                                    style={
                                      isSuperAdmin
                                        ? { borderColor: '#7C3AED', color: '#7C3AED', background: '#F5F3FF' }
                                        : isOwner
                                        ? { borderColor: 'var(--accent-primary)', color: 'var(--accent-primary)' }
                                        : {}
                                    }
                                    data-testid={`user-role-badge-${u.id}`}
                                  >
                                    {isSuperAdmin && <Shield className="h-2.5 w-2.5 mr-1 inline" />}
                                    {ROLE_MAP[u.role] || u.role}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="secondary" className={`text-[10px] ${u.is_active !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                                  {u.is_active !== false ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground tabular-nums">{u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</TableCell>
                              <TableCell className="text-xs text-muted-foreground tabular-nums">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Nunca'}</TableCell>
                              <TableCell className="text-right">
                                {!isSelf && !isProtected && (
                                  <div className="flex items-center justify-end gap-1">
                                    {editingUser === u.id ? (<>
                                      <Button size="sm" className="h-7 text-xs" onClick={() => handleSaveUser(u.id)}>Guardar</Button>
                                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditingUser(null)}>Cancelar</Button>
                                    </>) : (<>
                                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Editar rol" onClick={() => { setEditingUser(u.id); setEditRole(u.role); }}><Users className="h-3.5 w-3.5" /></Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7" title={u.is_active !== false ? 'Desactivar' : 'Activar'} onClick={() => handleToggleUser(u.id, u.is_active !== false)}>
                                        {u.is_active !== false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" title="Eliminar" onClick={() => handleDeleteUser(u.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                                    </>)}
                                  </div>
                                )}
                                {isSelf && isOwner && users.filter(x => x.id !== user?.id && x.is_active !== false).length > 0 && (
                                  <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setTransferTarget({})}>
                                    <Lock className="h-3 w-3 mr-1" /> Transferir
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Permissions matrix */}
                  <div className="mt-6">
                    <SectionTitle>Matriz de Permisos por Rol</SectionTitle>
                    <div className="rounded-xl border border-border overflow-hidden">
                      <Table>
                        <TableHeader><TableRow className="bg-muted/30 text-[10px]">
                          <TableHead className="text-xs">Funcionalidad</TableHead>
                          {Object.entries(ROLE_MAP).map(([k, v]) => <TableHead key={k} className="text-center text-[10px]">{v}</TableHead>)}
                        </TableRow></TableHeader>
                        <TableBody>
                          {PERM_MATRIX.map(row => (
                            <TableRow key={row.feature} className="text-xs">
                              <TableCell className="text-muted-foreground">{row.feature}</TableCell>
                              {Object.keys(ROLE_MAP).map(role => (
                                <TableCell key={role} className="text-center">
                                  {row[role] === 'full' ? <span style={{color:'var(--success)'}}>Total</span> :
                                   row[role] === 'read' ? <span className="text-muted-foreground">Lectura</span> :
                                   row[role] === 'own' ? <span style={{color:'var(--accent-primary)'}}>Propios</span> :
                                   row[role] === 'none' ? <span className="text-muted-foreground/40">—</span> : row[role]}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Audit log */}
                  <div className="mt-6">
                    <SectionTitle>Historial de acciones sobre usuarios</SectionTitle>
                    <p className="text-xs text-muted-foreground -mt-2 mb-3">Registro de creaciones, cambios de rol, activaciones y eliminaciones. Últimos 50 eventos.</p>
                    <UserAuditLog users={users} />
                  </div>
                </CardContent>
              </Card>

              {/* Transfer Ownership Dialog */}
              <Dialog open={!!transferTarget} onOpenChange={(open) => { if (!open) { setTransferTarget(null); setTransferPassword(''); } }}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-display">Transferir propiedad</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                      <p className="text-xs text-amber-800 font-medium">Esta acción cambiará tu rol a Administrador y asignará el rol de Propietario al usuario seleccionado.</p>
                    </div>
                    <Field label="Nuevo propietario">
                      <Select value={transferTarget?.id || ''} onValueChange={(v) => setTransferTarget(users.find(u => u.id === v) || {})}>
                        <SelectTrigger data-testid="transfer-target-select"><SelectValue placeholder="Seleccionar usuario" /></SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.id !== user?.id && u.is_active !== false).map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Tu contraseña actual" hint="Requerida para confirmar">
                      <Input type="password" value={transferPassword} onChange={(e) => setTransferPassword(e.target.value)} placeholder="Ingresa tu contraseña" data-testid="transfer-password-input" />
                    </Field>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { setTransferTarget(null); setTransferPassword(''); }}>Cancelar</Button>
                    <Button onClick={handleTransferOwnership} disabled={!transferTarget?.id || !transferPassword || transferring} variant="destructive" data-testid="transfer-confirm-btn">
                      {transferring ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Lock className="h-4 w-4 mr-1.5" />}
                      Transferir propiedad
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* ═══ SECURITY ═══ */}
            <TabsContent value="security">
              <Card className="rounded-xl border-border/50 shadow-sm">
                <CardContent className="p-6 space-y-6">
                  <SectionTitle>Cambiar Contraseña</SectionTitle>
                  <div className="max-w-md space-y-4">
                    <Field label="Contraseña actual">
                      <div className="relative">
                        <Input type={showPw ? 'text' : 'password'} value={pwForm.current} onChange={(e) => setPwForm({...pwForm, current: e.target.value})} />
                        <button className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPw(!showPw)}>{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                      </div>
                    </Field>
                    <Field label="Nueva contraseña" hint="Mínimo 6 caracteres">
                      <Input type="password" value={pwForm.newPw} onChange={(e) => setPwForm({...pwForm, newPw: e.target.value})} />
                    </Field>
                    <Field label="Confirmar nueva contraseña">
                      <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({...pwForm, confirm: e.target.value})} />
                    </Field>
                    <Button onClick={handleChangePassword} disabled={saving === 'password' || !pwForm.current || !pwForm.newPw}
                      style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90">
                      {saving === 'password' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Lock className="h-4 w-4 mr-1.5" />} Cambiar contraseña
                    </Button>
                  </div>
                  <Separator />
                  <SectionTitle>Sesión Actual</SectionTitle>
                  <div className="p-4 rounded-lg bg-muted/30 border border-border/50 max-w-md">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center"><Shield className="h-4 w-4 text-muted-foreground" /></div>
                      <div>
                        <p className="text-sm font-medium">{user?.full_name || user?.email}</p>
                        <p className="text-xs text-muted-foreground">{user?.email} — {ROLE_MAP[user?.role] || user?.role}</p>
                      </div>
                      <Badge variant="outline" className="ml-auto text-[10px]" style={{ borderColor: 'var(--success)', color: 'var(--success)' }}>Activa</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════
   Subscription / Licencias Tab
   ═══════════════════════════════════════════ */
const MODULE_CONFIG = [
  { key: 'financial', label: 'Financial Intelligence', icon: LineChart, color: 'var(--financial-primary)', light: 'var(--financial-light)', type: 'core', desc: 'Motor de análisis financiero, flujo de caja, TIR, VAN, sensibilidad, reportes y Copilot.' },
  { key: 'market', label: 'Market Intelligence', icon: TrendingUp, color: 'var(--market-primary)', light: 'var(--market-light)', type: 'addon', desc: 'Comparables, posicionamiento, pricing optimizer, absorción estimada, extractor de texto con IA.' },
  { key: 'construction', label: 'Construction Intelligence', icon: HardHat, color: 'var(--construction-primary)', light: 'var(--construction-light)', type: 'addon', desc: 'Seguimiento de avance de obra, control de costos, alertas de desviaciones.' },
];

function SubscriptionTab() {
  const { plan, modules, usage, isTrial, trialExpired, trialEnds, hasPermission, refresh } = useModules();
  const [toggling, setToggling] = useState(null);

  const handleToggle = async (mod, active) => {
    setToggling(mod);
    try {
      await companyAPI.updateModule(mod, active);
      toast.success(`${active ? 'Módulo activado' : 'Módulo desactivado'}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setToggling(null);
  };

  const trialDays = trialEnds ? Math.max(0, Math.ceil((new Date(trialEnds) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  return (
    <div className="space-y-6">
      {/* Plan Card */}
      <Card className="rounded-xl border-border/50 shadow-sm">
        <CardContent className="p-6">
          <SectionTitle>Plan actual</SectionTitle>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
              <Sparkles className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-display font-bold" style={{ color: 'var(--text-primary)' }}>{plan.label || 'Enterprise'}</h3>
                {isTrial && (
                  <Badge className="text-[10px]" style={{ background: 'var(--accent-subtle)', color: 'var(--accent-primary)', border: 'none' }}>
                    Trial — {trialDays} días restantes
                  </Badge>
                )}
                {trialExpired && (
                  <Badge className="text-[10px]" style={{ background: 'var(--warning-soft)', color: 'var(--warning-color)', border: 'none' }}>
                    Trial expirado
                  </Badge>
                )}
                {!isTrial && !trialExpired && (
                  <Badge className="text-[10px]" style={{ background: 'var(--success-soft)', color: 'var(--success)', border: 'none' }}>
                    Activo
                  </Badge>
                )}
              </div>
              <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {plan.description || 'Sin límites para equipos grandes'}
              </p>
            </div>
          </div>

          {/* Usage bars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <UsageBar label="Proyectos" current={usage.projects} max={usage.projects_limit} />
            <UsageBar label="Usuarios" current={usage.users} max={usage.users_limit} />
            <UsageBar label="Escenarios / proyecto" current="—" max={usage.scenarios_limit} isInfo />
          </div>
        </CardContent>
      </Card>

      {/* Modules */}
      <Card className="rounded-xl border-border/50 shadow-sm">
        <CardContent className="p-6">
          <SectionTitle>Módulos</SectionTitle>
          <div className="space-y-3">
            {MODULE_CONFIG.map(mod => {
              const Icon = mod.icon;
              const modData = modules[mod.key];
              const isActive = modData?.active !== false;
              const isCore = mod.type === 'core';
              const includedInPlan = (plan.included_modules || []).includes(mod.key);
              const isAddonPurchase = isActive && !isCore && !includedInPlan;
              const canManage = hasPermission('can_manage_modules');
              const isLoading = toggling === mod.key;

              return (
                <div key={mod.key} className="rounded-xl p-4 flex items-start gap-4 transition-all" style={{ border: `1px solid ${isActive ? mod.color + '25' : 'var(--border-default)'}`, background: isActive ? mod.light + '40' : 'var(--bg-secondary)' }} data-testid={`module-${mod.key}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: isActive ? mod.light : 'var(--bg-hover)' }}>
                    <Icon className="w-5 h-5" style={{ color: isActive ? mod.color : 'var(--text-muted)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{mod.label}</span>
                      {isCore && <Badge className="text-[8px]" style={{ background: 'var(--accent-subtle)', color: 'var(--accent-primary)', border: 'none' }}>Core</Badge>}
                      {!isCore && includedInPlan && <Badge className="text-[8px]" style={{ background: 'var(--success-soft)', color: 'var(--success)', border: 'none' }}>Incluido en {plan.label}</Badge>}
                      {isAddonPurchase && <Badge className="text-[8px]" style={{ background: 'var(--market-light)', color: 'var(--market-primary)', border: 'none' }}>Add-on</Badge>}
                      {!isCore && !includedInPlan && !isActive && <Badge className="text-[8px]" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)', border: 'none' }}>Add-on disponible</Badge>}
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>{mod.desc}</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {isActive ? (
                      <Badge className="text-[10px]" style={{ background: 'var(--success-soft)', color: 'var(--success)', border: 'none' }}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Activo
                      </Badge>
                    ) : (
                      <Badge className="text-[10px]" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)', border: 'none' }}>
                        <Lock className="w-3 h-3 mr-1" /> Inactivo
                      </Badge>
                    )}
                    {!isCore && canManage && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        disabled={isLoading}
                        onClick={() => handleToggle(mod.key, !isActive)}
                        style={isActive ? {} : { borderColor: mod.color, color: mod.color }}
                        data-testid={`toggle-${mod.key}`}
                      >
                        {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : isActive ? 'Desactivar' : 'Activar'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function UsageBar({ label, current, max, isInfo }) {
  const pct = isInfo ? 0 : (max > 0 ? Math.min(100, (current / max) * 100) : 0);
  const isHigh = pct > 80;
  return (
    <div className="p-3 rounded-lg" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)' }}>
      <div className="flex justify-between mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{label}</span>
        <span className="text-xs font-bold tabular-nums" style={{ color: isHigh ? 'var(--warning-color)' : 'var(--text-primary)' }}>
          {isInfo ? `Hasta ${max}` : `${current} / ${max >= 999 ? '∞' : max}`}
        </span>
      </div>
      {!isInfo && (
        <div className="h-1.5 rounded-full" style={{ background: 'var(--bg-hover)' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: isHigh ? 'var(--warning-color)' : 'var(--success)' }} />
        </div>
      )}
    </div>
  );
}
